import React, { useContext, useMemo } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import merge from 'lodash/merge';
import { useSelector } from 'react-redux';

import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { FieldContained } from '@ad-scripts/presentation/components/common/textarea_field';
import FormContext from 'src/presentation/contexts/FormContext';
import MemoSelector, { MemorySelectorResult } from './Rule/MemorySelector';
import {
  selectCurrentAnonModule,
  selectCurrentModule,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { StyledRow } from 'src/presentation/components/pages/sidekick/threads/components/Layout';
import { TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import { useCondition } from './Rule/useCondition';
import { ThreadModuleContext } from '../ThreadModuleContext';
import { Row } from './controls/Layout';

export default function FilterField({ attr, typeSpec }: { attr: string; typeSpec: TypeSpec }) {
  const { readOnly, isAnon: isAnonModule } = useContext(ThreadModuleContext);

  let module = useSelector(selectCurrentModule);
  const anonModule = useSelector(selectCurrentAnonModule);

  if (isAnonModule && anonModule) {
    module = anonModule;
  }

  const isFilterByRule = module.name === 'filterByRule' || module.name === 'filterData';

  const notChangeableFields = useMemo<string[]>(
    () => (get(module, 'controlValues.data') || []).filter(key => !!key),
    [module]
  );

  const { workingState, onBlur } = useContext(FormContext);

  const fields = useMemo(() => {
    const fields = get(workingState, attr) || [];
    if (notChangeableFields?.length) {
      return merge(fields, notChangeableFields);
    }
    return fields;
  }, [attr, notChangeableFields, workingState]);

  const { currentFields, setCurrentFields, optGroups } = useCondition({
    fields,
    allowEnterManually: false,
    compatibilityMatch: false,
    includeOnlyNestedProps: false,
  });

  const handleChangeMemoFields = props => {
    const currentProperties = props.currentState;

    const value = get(currentProperties, attr, []);
    const newProps = { currentState: set(currentProperties, attr, value) };
    onBlur && onBlur(newProps);
  };

  const showCondition = !isFilterByRule || notChangeableFields.length;

  if (!showCondition) {
    return (
      <Row>
        <AlertWarning>Please select data to filter</AlertWarning>
      </Row>
    );
  }

  return (
    <Row>
      <FieldContained
        attr={attr}
        label={typeSpec.prompt}
        control={
          <StyledRow>
            <MemoSelector
              attr={attr}
              disabled={readOnly}
              isEnterManually={false}
              optGroups={optGroups}
              fields={currentFields}
              setCurrentFields={setCurrentFields}
              onChange={handleChangeMemoFields}
              resultType={MemorySelectorResult.ARRAY}
              notChangeableFields={notChangeableFields}
            />
          </StyledRow>
        }
      />
    </Row>
  );
}
